import React, { Suspense, useEffect } from 'react';
import { PageSkeleton } from './PageSkeleton';
import { RouteErrorBoundary } from '@/components/error/RouteErrorBoundary';
import { OptimizedPageSkeleton } from '@/components/ui/enhanced-loading/OptimizedPageSkeleton';
import { diagnosticMonitor } from '@/utils/diagnostic-performance';

interface LazyComponentWrapperProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  routeName: string;
  preserveProgress?: boolean;
}

export function LazyComponentWrapper({
  children,
  fallback,
  routeName,
  preserveProgress = false
}: LazyComponentWrapperProps) {
  // Start diagnostic measurement when wrapper mounts (bundle download starts)
  useEffect(() => {
    diagnosticMonitor.startRouteMeasure(routeName);
    
    return () => {};
  }, [routeName]);
  
  // Track when children change (component loaded)
  useEffect(() => {
    // Component loaded tracking
  }, [children, routeName]);

  const getOptimizedFallback = () => {
    if (fallback) return fallback;
    
    // Choose appropriate skeleton variant based on route
    const skeletonVariants: Record<string, any> = {
      'dashboard': { variant: 'dashboard', showHeader: true },
      'profile': { variant: 'profile', showHeader: true },
      'clients': { variant: 'table', showHeader: true },
      'admin': { variant: 'table', showHeader: true },
      'settings': { variant: 'form', showHeader: true },
      'company': { variant: 'form', showHeader: true },
      'assessment': { variant: 'form', showHeader: true },
      'tasks': { variant: 'card-grid', showHeader: true },
      'learn': { variant: 'card-grid', showHeader: true },
      'plan': { variant: 'dashboard', showHeader: true },
      'valuations': { variant: 'dashboard', showHeader: true },
      'benchmarks': { variant: 'dashboard', showHeader: true },
      'successors': { variant: 'dashboard', showHeader: true },
      'documents': { variant: 'card-grid', showHeader: true },
      'team': { variant: 'dashboard', showHeader: true },
    };
    
    const config = skeletonVariants[routeName] || { variant: 'dashboard', showHeader: true };
    return <OptimizedPageSkeleton {...config} />;
  };

  return (
    <RouteErrorBoundary routeName={routeName} preserveProgress={preserveProgress}>
      <Suspense fallback={getOptimizedFallback()}>
        {children}
      </Suspense>
    </RouteErrorBoundary>
  );
}

// HOC for wrapping lazy components
export function withLazyWrapper<T extends Record<string, any>>(
  routeName: string,
  preserveProgress = false,
  fallback?: React.ReactNode
) {
  return function<P extends T>(Component: React.ComponentType<P>) {
    const WrappedComponent = (props: P) => (
      <LazyComponentWrapper 
        routeName={routeName} 
        preserveProgress={preserveProgress}
        fallback={fallback}
      >
        <Component {...props} />
      </LazyComponentWrapper>
    );
    
    WrappedComponent.displayName = `LazyWrapper(${routeName})`;
    return WrappedComponent;
  };
}